/* eslint-disable no-nested-ternary */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import config from 'config';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { useBreakpoint } from '@techstyle/react-components';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';
import { getNewDateFunction } from '@techstyle/redux-core';

import {
  addBasketItem,
  deleteBasketItem,
  addProductSetToCart,
  updateCartItem,
  sweepCart,
  loadCart,
} from '../../actions/checkout';
import {
  trackQuickViewUpdateCart,
  trackQuickViewAddToCart,
  trackQuickViewClickFullDetails,
  trackQuickViewChangeProductAttribute,
} from '../../actions/quickView';
import { MembershipActionLocation } from '../../constants/checkout';
import toastWrapper from '../../hoc/toastWrapper';
import { desktop, mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import {
  findBraSizeCategory,
  getSetItemLinkedProduct,
  hasLinkedProduct,
  checkPreorderForSingleItem,
} from '../../utils/productDetail';
import {
  getProductDiff,
  formatSizeListForOneSize,
  getModalMarginLimits,
} from '../../utils/quickView';
import {
  getPriceDetails,
  isSoldOut as checkIsSoldOut,
  isUnbreakableSet as checkIsUnbreakableSet,
  isXtraVipBox as checkIsXtraVipBox,
  shouldHideRelatedProducts as checkShouldHideRelatedProducts,
} from '../../utils/selectors';
import { hasBraSize } from '../../utils/sizes';
import { useAutoBundle2 } from '../../utils/useAutoBundle2';
import { Component as ByoCTAs } from '../ByoCTAs';
import { useByoContext } from '../ByoProvider';
import { Component as Carousel } from '../Carousel';
import { useDrawerState } from '../CartDrawer/useDrawerState';
import { Component as CloseButton } from '../CloseButton';
import { Component as ColorSwatch } from '../ColorSwatch';
import NewThemeIcon from '../NewThemeIcon/NewThemeIcon';
import { Container as PreorderShipDate } from '../PreorderShipDate';
import { Component as ProductDetailColorSwatches } from '../ProductDetailColorSwatches';
import { Component as ProductDetailCTA } from '../ProductDetailCTA';
import { Component as ProductDetailDescription } from '../ProductDetailDescription';
import { Component as ProductDetailFlag } from '../ProductDetailFlag';
import { Container as ProductDetailHeader } from '../ProductDetailHeader';
import { Component as ProductDetailName } from '../ProductDetailName';
import { Container as ProductDetailSize } from '../ProductDetailSize';
import { Component as ProductDetailSizeSelector } from '../ProductDetailSizeSelector';
import { Component as ProductFlag } from '../ProductFlag';
import { Component as ProductImage } from '../ProductImage';
import { Component as ProductLink } from '../ProductLink';
import { Component as ProductSetDetailOptionSummary } from '../ProductSetDetailOptionSummary';
import { useQuickViewContext } from '../QuickViewProvider';
import { Component as SpinningLoader } from '../SpinningLoader';
import ToastMessages from '../ToastMessages/ToastMessages';

const setId = config.get('public.productTypes.setId');
const curvySizeTag = config.get('public.productTags.plusSize');

const MESSAGE_CODES = {
  GENERAL_ERROR: 'GENERAL_ERROR',
  SIZE_UNAVAILABLE: 'SIZE_UNAVAILABLE',
  SELECT_CUP_SIZE: 'SELECT_CUP_SIZE',
  SIZE_REQUIRED: 'SIZE_REQUIRED',
  ITEM_ADDED: 'ITEM_ADDED',
  ITEM_UPDATED: 'ITEM_UPDATED',
};

const ModalTouchAreas = {
  MODAL_HEADER: 'header',
  MODAL_CONTENT: 'content',
};

const dialogStyle = css`
  ${desktop`
    position:relative;
    width: 800px;
    height: 600px;
    overflow: hidden;
    padding: 28px 0 20px 28px;
    z-index: 1200;
  `}
  width: 100%;
  height: 100%;
`;

const StyledModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
})`
  ${desktop`
    // since the modal should show above the side drawer on desktop
    z-index: 9999;
  `}
  ${mobile`
    ${BasicModal.Wrapper} {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      margin-top: ${({ margin }) => margin}px;
    }
  `}
  ${desktop`
    ${({ isOpen }) => isOpen && 'z-index: 1200;'}  
  `}
`;

const ModalContent = styled.div`
  height: 100%;
  display: flex;
  ${mobile`
    flex-direction: column;
    overflow-y: ${({ margin }) => (margin <= 0 ? 'scroll' : 'hidden')};
    overflow-x: hidden;
    overscroll-behavior: none;
  `};
`;

const MobileModalHeader = styled.div`
  height: 90px;
  width: 100%;
  border-radius: ${({ mobileFullScreen }) =>
    mobileFullScreen ? '0' : '8px 8px 0 0'};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing.small}px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const StyledProductFlag = styled(ProductDetailFlag)`
  position: relative;

  ${ProductFlag.Wrapper} {
    z-index: 1;
    margin-top: ${({ theme }) => theme.spacing.small}px;
    margin-left: 10px;
    position: absolute;
  }
`;

const MobileHeaderImageWrapper = styled.div`
  margin-right: 12px;
`;

const LeftColumn = styled.div`
  ${desktop`
    width: 400px;
  `}
`;

const RightColumn = styled.div`
  padding-bottom: 20px;
  ${desktop`
    height: 100%;
    overflow-y: scroll;
    padding: 0 20px;
    ::-webkit-scrollbar {
      background-color: transparent;
      width: 10px;
    }
    ::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colors.gray250};
      border-radius: 5px;
    }
  `};
`;

const ProductDetailsWrapper = styled.div`
  ${mobile`
    padding: 0 ${({ theme }) => theme.spacing.small}px;
  `}
`;

const ImageCarousel = styled(Carousel)`
  .slick-disabled {
    display: none !important;
    pointer-events: none;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  z-index: ${({ theme }) => theme.zIndex.increment};

  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.white};

  ${mobile`
    display: none !important;
  `}
`;

const PrevButton = styled(ArrowButton).attrs({
  [`aria-label`]: 'Previous',
})`
  cursor: w-resize;

  left: 16px;
`;

const ProductDetailDescriptionWrapper = styled.div`
  ${ProductDetailDescription.DescriptionWrapper} {
    padding: ${({ theme }) => theme.spacing.tiny}px 0 0;
  }

  ${ProductDetailDescription.StyledProductDescription} {
    padding: 0;
  }

  ${ProductDetailDescription.StyledCollapsibleButton} {
    border-top: 3px solid black;
    padding-left: 0;
    ${mobile`
    padding: ${({ theme }) => theme.spacing.tiny}px 20px ${({ theme }) =>
      theme.spacing.small}px;
    width: calc(100% + 40px);
    margin-left: -20px;
    `}
  }
`;

const NextButton = styled(ArrowButton).attrs({
  [`aria-label`]: 'Next',
})`
  right: 16px;

  cursor: e-resize;
`;

const SingleProductColorSwatches = styled(ProductDetailColorSwatches)`
  & ${ColorSwatch.ColorTitle} {
    color: ${({ theme }) => theme.colors.subdued};
    display: flex;
    align-items: flex-end;
    font-size: 12px;
    margin-bottom: 0;
    margin-right: ${({ theme }) => theme.spacing.tiny}px;
  }

  & ${ColorSwatch.ColorName} {
    display: flex;
  }
`;

const UpdateButton = styled(BasicButton).attrs({
  fullWidth: true,
})`
  min-width: 130px;
  margin: ${({ isSet, theme }) =>
      isSet ? theme.spacing.small : theme.spacing.tiny}px
    0 ${({ theme }) => theme.spacing.small}px;
`;

const StyledLink = styled.a`
  display: flex;
  justify-content: center;
  cursor: pointer;

  ${({ theme }) => theme.button.variants.secondaryLavender.buttonStyle}
`;

const StyledRemoveLink = styled.a`
  display: flex;
  justify-content: center;
  cursor: pointer;

  text-align: center;
  font-family: Plain;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.red500};
`;
const SetItemWrapper = styled.div``;

const SetItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const SetItem = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex-wrap: nowrap;
  margin-top: ${({ theme }) => theme.spacing.small}px;

  border: solid 1px ${props => props.theme.colors.stone};

  padding: ${({ theme }) => theme.spacing.small}px;
`;

const Row = styled.div`
  display: flex;
`;

const SetItemName = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.small}px;
  align-self: center;
  ${({ theme }) => theme.subHeader.variants.subHeading2Uppercase.textStyles}
  color: ${props => props.theme.colors.gunmetal};
`;

const RowCollapsible = styled(Row)`
  flex-direction: row;
`;
const StyledProductImage = styled(ProductImage)`
  padding-bottom: 20px;
  ${mobile`
    padding-right: 4px;
  `};
`;

const ProductImageThumbnail = styled(ProductImage)`
  display: block;
  height: 81px;
  width: auto;
  margin-right: ${({ theme }) => theme.spacing.medium}px;

  ${mobile`
    width: auto;
    height: 115px;
  `};
`;

const MobileHeaderProductImage = styled(ProductImageThumbnail)`
  height: 66px;
  margin-right: 0;
`;

const StyledProductDetailSize = styled(ProductDetailSize)`
  & ${ProductDetailSizeSelector.SizeCategoryWrapper} {
    margin-bottom: 0;
  }

  & ${ProductDetailSizeSelector.SizeSectionWrap} {
    margin-top: 0;
    margin-bottom: 12px;
  }

  & ${ProductDetailSizeSelector.SizeCategoryLabel} {
    margin: 12px 0;
  }

  & ${ProductDetailSize.SizeContainer} {
    ${mobile` margin-top: 4px;
    `}
  }
`;

const StyledProductDetailName = styled(ProductDetailName)`
  ${ProductDetailName.ProductName} {
    max-width: 260px;
  }
`;

const ProductSetDetailOptionSummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SetItemDetail = styled.div`
  flex: 1;
  width: 100%;
`;

const StyleProductLink = styled(ProductLink)`
  width: 100%;

  margin: auto;
`;

const StyledPreorderShipDate = styled(PreorderShipDate)`
  color: ${props => props.theme.colors.red500};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const StyledToastMessages = styled(ToastMessages)`
  position: absolute;
  top: 0;
`;

function QuickViewModal({
  showToastMessage,
  hideToastMessage,
  mobileFullScreen = false,
  hideAfterpay = false,
}) {
  const { formatMessage } = useIntl();

  const {
    isOpen,
    isLoading,
    defaultItem,
    closeQuickViewModal,
    closeQuickViewModalForBundleAlert,
    openBundleAlertModal,
    currentProduct,
    setCurrentProduct,
    selections,
    setSelections,
    isAddToCartGwpEnabled,
    isAddToCartUpsellEnabled,
    isUpdateCartItemEnabled,
    isUpdateCartItemGwpEnabled,
    isGwpModeEnabled,
    setStartBundleUpdateLoading,
    setStopBundleUpdateLoading,
    source,
  } = useQuickViewContext();

  const dispatch = useDispatch();
  const { isMobile, isDesktop } = useBreakpoint();
  const isProductSet = currentProduct?.product_type_id === setId;
  const getNewDate = useSelector(getNewDateFunction);

  const isCurvy = currentProduct?.tag_id_list.includes(curvySizeTag);
  const isBorderfreeCustomer = useSelector(state => state.isBorderfreeCustomer);
  const isSoldOut = useSelector(state =>
    currentProduct ? checkIsSoldOut(state, { product: currentProduct }) : false
  );
  const isUnbreakableSet = useSelector(state =>
    currentProduct
      ? checkIsUnbreakableSet(state, { product: currentProduct })
      : false
  );
  const isXtraVipBox = useSelector(state =>
    currentProduct
      ? checkIsXtraVipBox(state, { product: currentProduct })
      : false
  );
  const shouldHideRelatedProducts = useSelector(state =>
    currentProduct
      ? checkShouldHideRelatedProducts(state, { product: currentProduct })
      : false
  );
  const { isFlowInitiated } = useByoContext();

  const defaultModalMarginTop = mobileFullScreen && isMobile ? 0 : 100;
  const [initialTouchX, setInitialTouchX] = useState(null);
  const [initialTouchY, setInitialTouchY] = useState(null);
  const [initialMargin, setInitialMargin] = useState(defaultModalMarginTop);
  const [currentMargin, setCurrentMargin] = useState(initialMargin);
  const modalContentRef = useRef(null);

  const autoBundle2Enabled = useAutoBundle2();

  const showAutoBundleVersion =
    !autoBundle2Enabled ||
    (autoBundle2Enabled && !defaultItem?.componentLineId);

  const handleCloseQuickViewModal = useCallback(() => {
    hideToastMessage();
    closeQuickViewModal();
    setCurrentMargin(defaultModalMarginTop);
  }, [
    closeQuickViewModal,
    defaultModalMarginTop,
    hideToastMessage,
    setCurrentMargin,
  ]);

  // These handleTouch functions are to control the behavior of the modal sliding up to the top of the screen
  // and the modal content scrolling, and how those behaviors coexist

  const handleTouchStart = useCallback(
    (e, touchArea) => {
      if (
        modalContentRef.current.scrollTop === 0 ||
        touchArea === ModalTouchAreas.MODAL_HEADER ||
        currentMargin > 0
      ) {
        setInitialMargin(currentMargin);
        setInitialTouchX(e.touches[0].clientX);
        setInitialTouchY(e.touches[0].clientY);
      }
    },
    [currentMargin]
  );

  const handleTouchMove = useCallback(
    e => {
      if (initialTouchY === null) {
        return;
      }
      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const swipeDistanceX = touchX - initialTouchX;
      const swipeDistanceY = touchY - initialTouchY;

      const newMargin = initialMargin + swipeDistanceY;

      // These checks are to prevent the modal from moving small amounts on the vertical plane when the user scrolls
      // horizontally across the product images
      if (
        Math.abs(swipeDistanceY) > 20 &&
        Math.abs(swipeDistanceY) > Math.abs(swipeDistanceX)
      ) {
        setCurrentMargin(getModalMarginLimits(newMargin));
      }
    },
    [initialMargin, initialTouchX, initialTouchY]
  );

  const handleTouchEnd = useCallback(() => {
    // When open, the modal drawer should either be at the original height or the top of the page, whichever the swipe ended closest to
    // If the user swipes more than 40% down the screen while the modal is open, the modal should close
    if (currentMargin <= defaultModalMarginTop / 2) {
      setCurrentMargin(0);
    } else if (
      currentMargin >= defaultModalMarginTop / 2 &&
      currentMargin <= window.screen.height * 0.6
    ) {
      setCurrentMargin(defaultModalMarginTop);
    } else {
      handleCloseQuickViewModal();
    }
    setInitialTouchY(null);
    setInitialTouchX(null);
  }, [currentMargin, defaultModalMarginTop, handleCloseQuickViewModal]);

  const currentProductMasterProductId =
    currentProduct?.master_product_id || currentProduct?.masterProductId;

  // This will be used to add error messages to the selectors after the user has
  // pressed a button to add or update the cart. There are some messages we don't
  // want to display until the user has interacted with the selectors or pressed the button
  const setErrorsAfterSubmit = useCallback(() => {
    const newSelections = { ...selections };
    Object.keys(newSelections).forEach(id => {
      const { band, cup, value } = newSelections[id].size;

      if (!value) {
        newSelections[id].size.error = MESSAGE_CODES.SIZE_REQUIRED;
      }

      // This is used to update error messages for `ProductDetailSizeSelector`
      // This makes sure we don't display an error message like "cup selection required"
      // until the user has pressed the button.
      if (band && !cup) {
        newSelections[id].size.error = MESSAGE_CODES.SELECT_CUP_SIZE;
      }
    });

    setSelections(newSelections);
  }, [selections, setSelections]);

  const handleSizeChange = useCallback(
    (option, id, field) => {
      let newBand;
      let newCup;
      let error;
      let newMasterProduct = currentProduct;
      const mapKey = isProductSet ? id : defaultItem.masterProductId;
      const color = selections[mapKey].color;
      const sizeSelectionNotCompleted =
        field === 'bandSize' || field === 'cupSize';

      const newSizeProduct = sizeSelectionNotCompleted
        ? null
        : color.sizes.find(size => {
            const conditionForMutipleOneSize = selections[mapKey]
              .hasMultipleOneSizes
              ? size.product_id === option?.productId
              : true;
            return (
              (size.size === option?.value || size.size === option) &&
              conditionForMutipleOneSize
            );
          });

      // don't change product images for sets when switching missy/curvy sizes to match pdp experience
      if (!isProductSet) {
        const linkedColorProduct = Array.isArray(color.linkedProduct)
          ? color.linkedProduct[0]
          : color.linkedProduct;
        const productDiff = newSizeProduct
          ? getProductDiff({ color, linkedColorProduct, newSizeProduct })
          : {};

        newMasterProduct = {
          ...currentProduct,
          ...productDiff,
        };
        setCurrentProduct(newMasterProduct);
      }
      if (field === 'bandSize') {
        newBand = option;

        // If a cup size was previously selected, we will display a message saying to
        // choose a cp size automatically. This error message won't display if the user
        // has not selected a cup size yet.
        const currentSize = selections[mapKey].size;
        if (currentSize.cup) {
          error = MESSAGE_CODES.SELECT_CUP_SIZE;
        } else {
          error = null;
        }
      } else if (field === 'cupSize') {
        newCup = option;
        error = null;
      } else {
        if (field === 'braSize') {
          const { band, cup } = findBraSizeCategory(option?.value || option);
          newBand = band;
          newCup = cup;
        }
        const availableQuantity = checkPreorderForSingleItem({
          product: newMasterProduct,
          now: getNewDate,
        })
          ? newSizeProduct?.available_quantity_preorder
          : newSizeProduct?.available_quantity;
        error = availableQuantity > 0 ? null : MESSAGE_CODES.SIZE_UNAVAILABLE;
      }
      const newSelections = {
        ...selections,
        [mapKey]: {
          ...selections[mapKey],
          color,
          size: {
            sizeProduct: newSizeProduct,
            value: sizeSelectionNotCompleted ? null : option?.value || option,
            error,
            band: newBand,
            cup: newCup,
          },
        },
      };
      const isSizeChanged =
        selections[mapKey].size.value !== newSelections[mapKey].size.value;
      if (!sizeSelectionNotCompleted && newSizeProduct && isSizeChanged) {
        dispatch(
          trackQuickViewChangeProductAttribute({
            label: 'size',
            previous_value: selections[mapKey].size.value,
            new_value: newSelections[mapKey].size.value,
            previous_product_id:
              selections[mapKey].size.sizeProduct?.product_id || null,
            new_product_id: newSizeProduct?.product_id || null,
          })
        );
      }
      setSelections(newSelections);
    },
    [
      defaultItem,
      currentProduct,
      dispatch,
      getNewDate,
      isProductSet,
      selections,
      setCurrentProduct,
      setSelections,
    ]
  );

  const handleColorChange = useCallback(
    (event, color, id) => {
      let newMasterProduct = currentProduct;
      const mapKey = isProductSet ? id : defaultItem.masterProductId;
      const newSizeProduct = color.sizes.find(
        size => size.size === selections[mapKey].size.value
      );
      // don't change product images for sets when switching colors to match pdp experience
      if (!isProductSet) {
        const linkedColorProduct = Array.isArray(color.linkedProduct)
          ? color.linkedProduct[0]
          : color.linkedProduct;

        const productDiff = newSizeProduct
          ? getProductDiff({ color, linkedColorProduct, newSizeProduct })
          : {
              permalink: color.permalink,
              item_number: color.itemNumber,
              date_preorder_expires: color.datePreorderExpires,
            };
        newMasterProduct = {
          ...currentProduct,
          ...productDiff,
        };
        setCurrentProduct(newMasterProduct);
      }

      const availableQuantity = checkPreorderForSingleItem({
        product: newMasterProduct,
        now: getNewDate,
      })
        ? newSizeProduct?.available_quantity_preorder
        : newSizeProduct?.available_quantity;

      const newSelections = {
        ...selections,
        [mapKey]: {
          ...selections[mapKey],
          id,
          color,
          size:
            newSizeProduct && !selections[mapKey].hasMultipleOneSizes
              ? {
                  ...selections[mapKey].size,
                  sizeProduct: newSizeProduct,
                  error: availableQuantity > 0 ? null : 'SIZE_UNAVAILABLE',
                }
              : {
                  sizeProduct: null,
                  value: null,
                  error: null,
                  band: null,
                  cup: null,
                },
        },
      };
      dispatch(
        trackQuickViewChangeProductAttribute({
          label: 'color',
          previous_value: selections[mapKey].color?.name,
          new_value: color.name,
          previous_product_id:
            selections[mapKey].size.sizeProduct?.product_id || null,
          new_product_id: newSizeProduct?.product_id || null,
        })
      );
      setSelections(newSelections);
    },
    [
      defaultItem,
      currentProduct,
      dispatch,
      getNewDate,
      isProductSet,
      selections,
      setCurrentProduct,
      setSelections,
    ]
  );

  const productsToAdd = useMemo(() => {
    if (!selections) {
      return [];
    }

    return Object.keys(selections)
      .map(id => selections[id].size.sizeProduct)
      .filter(sizeProduct => sizeProduct);
  }, [selections]);

  const currentErrorCode = useMemo(() => {
    if (!selections) {
      return null;
    }

    let firstError;

    Object.keys(selections).some(id => {
      const { error } = selections[id].size;
      if (error) {
        firstError = error;
        return true;
      }
    });

    return firstError;
  }, [selections]);

  const isUpdateCartEnabled = useMemo(() => {
    const areProductsReady = isProductSet
      ? productsToAdd.length === defaultItem.bundleItems.length
      : !!productsToAdd.length;

    return areProductsReady && !currentErrorCode;
  }, [defaultItem, isProductSet, productsToAdd, currentErrorCode]);

  const showToastMessageCode = useCallback(
    errorCode => {
      setErrorsAfterSubmit();

      let message;

      switch (errorCode) {
        case MESSAGE_CODES.SIZE_UNAVAILABLE:
          message = (
            <FormattedMessage
              id="global_checkout.quick_view_size_unavailable_error_message"
              defaultMessage="The selected size is unavailable"
            />
          );
          break;
        case MESSAGE_CODES.SELECT_CUP_SIZE:
          message = (
            <FormattedMessage
              id="global_checkout.quick_view_select_cup_size_error_message"
              defaultMessage="Select a cup size"
            />
          );
          break;
        case MESSAGE_CODES.ITEM_ADDED:
          message = (
            <FormattedMessage
              id="global_checkout.quick_view_add_successful_message"
              defaultMessage="Your item was added successfully!"
            />
          );
          break;
        case MESSAGE_CODES.ITEM_UPDATED:
          message = (
            <FormattedMessage
              id="global_checkout.quick_view_update_successful_message"
              defaultMessage="Your item was updated successfully!"
            />
          );
          break;
        case MESSAGE_CODES.GENERAL_ERROR:
          message = (
            <FormattedMessage
              id="global_checkout.general_error"
              defaultMessage="Oops we’ve encountered an error. Try again"
            />
          );
          break;
        case MESSAGE_CODES.SELECT_A_SIZE:
        default:
          message = (
            <FormattedMessage
              id="site_pdp.size_required_error_message"
              defaultMessage="Select a size"
            />
          );
          break;
      }

      showToastMessage(message);
    },
    [showToastMessage, setErrorsAfterSubmit]
  );

  const handleRemoveFromBundle = async () => {
    closeQuickViewModalForBundleAlert();
    openBundleAlertModal();
  };

  const handleUpdateBundleItem = async () => {
    const productsToAdd = Object.keys(selections)
      .map(id => selections[id].size.sizeProduct)
      .filter(sizeProduct => sizeProduct);
    const areProductsReady = isProductSet
      ? productsToAdd.length === defaultItem.bundleItems.length
      : !!productsToAdd.length;
    const hasNoErrors = Object.keys(selections).some(
      id => !selections[id].size.error
    );
    if (areProductsReady && hasNoErrors) {
      handleCloseQuickViewModal();
      try {
        // Delete Item from Bundle
        setStartBundleUpdateLoading();
        await dispatch(
          deleteBasketItem(
            defaultItem.cartLineId,
            MembershipActionLocation.MINI_CART,
            false,
            defaultItem.componentLineId
          )
        );

        await dispatch(
          addBasketItem(productsToAdd[0].product_id, 1, {
            location: MembershipActionLocation.OTHER,
          })
        );

        if (autoBundle2Enabled) {
          await dispatch(sweepCart());
        }

        // Add Updated item back to cart and (then sweep if auto-bundle-2) it should autobundle
        setStopBundleUpdateLoading();
        showToastMessageCode(MESSAGE_CODES.ITEM_ADDED);
        dispatch(trackQuickViewUpdateCart());
      } catch (err) {
        setStopBundleUpdateLoading();
        Sentry.captureException(err);
        showToastMessageCode(MESSAGE_CODES.GENERAL_ERROR);
      }
    } else {
      showToastMessageCode(MESSAGE_CODES.SELECT_A_SIZE);
    }
  };

  const handleUpdateCartItem = useCallback(async () => {
    if (isUpdateCartEnabled) {
      handleCloseQuickViewModal();
      const payload = isProductSet
        ? {
            setId: currentProduct.master_product_id,
            componentProductIds: productsToAdd.map(
              product => product.product_id
            ),
          }
        : {
            productId: productsToAdd[0].product_id,
          };
      try {
        if (currentProduct?.lineItems?.length > 0) {
          for (const lineItem of currentProduct.lineItems) {
            await dispatch(
              updateCartItem(isProductSet, lineItem.cartLineId, payload, true)
            );
          }
          await dispatch(loadCart());
        } else {
          await dispatch(
            updateCartItem(isProductSet, defaultItem.cartLineId, payload)
          );
        }
        showToastMessageCode(MESSAGE_CODES.ITEM_UPDATED);
        dispatch(trackQuickViewUpdateCart());
      } catch (err) {
        Sentry.captureException(err);
        showToastMessageCode(MESSAGE_CODES.GENERAL_ERROR);
      }
    } else {
      setErrorsAfterSubmit();
      showToastMessageCode(currentErrorCode);
    }
  }, [
    isUpdateCartEnabled,
    handleCloseQuickViewModal,
    isProductSet,
    currentProduct,
    productsToAdd,
    dispatch,
    defaultItem,
    showToastMessageCode,
    setErrorsAfterSubmit,
    currentErrorCode,
  ]);

  const handleAddToCart = useCallback(async () => {
    if (isUpdateCartEnabled) {
      handleCloseQuickViewModal();

      let location = MembershipActionLocation.OTHER;

      if (isAddToCartUpsellEnabled) {
        location = MembershipActionLocation.UPSELLS;
      }

      try {
        if (isProductSet) {
          const { master_product_id: masterProductId } = currentProduct;
          await dispatch(
            addProductSetToCart(
              masterProductId,
              productsToAdd.map(product => product.product_id),
              1,
              location,
              source
            )
          );
        } else {
          await dispatch(
            addBasketItem(
              productsToAdd[0].product_id,
              1,
              {
                location,
                source,
              },
              source
            )
          );
        }
        showToastMessageCode(MESSAGE_CODES.ITEM_ADDED);

        let eventMessage;
        // TODO: Add message for GWP
        if (isAddToCartUpsellEnabled) {
          eventMessage = 'Upsells Add to Cart';
        }
        if (eventMessage) {
          dispatch(trackQuickViewAddToCart(eventMessage));
        }
      } catch (err) {
        Sentry.captureException(err);
        showToastMessageCode(MESSAGE_CODES.GENERAL_ERROR);
      }
    } else {
      setErrorsAfterSubmit();
      showToastMessageCode(currentErrorCode);
    }
  }, [
    isUpdateCartEnabled,
    handleCloseQuickViewModal,
    isProductSet,
    showToastMessageCode,
    isAddToCartUpsellEnabled,
    currentProduct,
    dispatch,
    productsToAdd,
    source,
    setErrorsAfterSubmit,
    currentErrorCode,
  ]);

  const [, setIsDrawerOpen] = useDrawerState();
  const handleDetailsLinkClick = useCallback(() => {
    setIsDrawerOpen(false);
    dispatch(trackQuickViewClickFullDetails());
    handleCloseQuickViewModal();
    return false;
  }, [dispatch, handleCloseQuickViewModal, setIsDrawerOpen]);

  const renderSetDetails = () => {
    return currentProduct.products_in_set.map((product, index) => {
      const { master_product_id: masterProductId } = product;
      const currentSelection = selections[masterProductId];
      const isLinkedSize =
        currentSelection.size.sizeProduct &&
        currentSelection.size.sizeProduct.is_linked_size;
      const setItemLinkedProduct = getSetItemLinkedProduct(
        product,
        currentSelection.color.value,
        isLinkedSize
      );
      const setItemLabel = isLinkedSize
        ? setItemLinkedProduct.label
        : product.label;

      return (
        <SetItemWrapper key={index}>
          <SetItem>
            <SetItemHeader>
              <Row>
                <SetItemName>{setItemLabel}</SetItemName>
              </Row>
              <RowCollapsible>
                <ProductImageThumbnail
                  angle="LAYDOWN"
                  product={
                    isLinkedSize ? setItemLinkedProduct : currentSelection.color
                  }
                  size={6}
                />
                <ProductSetDetailOptionSummaryWrapper>
                  <ProductSetDetailOptionSummary
                    setItem={product}
                    isSoldOut={isSoldOut}
                    currentSelection={currentSelection}
                  >
                    <SetItemDetail>
                      <ProductDetailColorSwatches
                        id={masterProductId}
                        isBorderfreeCustomer={isBorderfreeCustomer}
                        isSoldOut={isSoldOut}
                        isUnbreakableSet={isUnbreakableSet}
                        isXtraVipBox={isXtraVipBox}
                        limit={10}
                        masterProduct={product}
                        onChangeColor={handleColorChange}
                        selectedColor={currentSelection.color.value}
                        selectedColorLabel={currentSelection.color.name}
                        selectedSize={currentSelection.size}
                        showSelectedOnly={false}
                        setId={product.master_product_id}
                      />

                      <StyledProductDetailSize
                        disabled={isXtraVipBox && isSoldOut}
                        error={currentSelection.size.error}
                        hideLabel
                        hideQuantity
                        id={masterProductId}
                        isSoldOut={isSoldOut}
                        onChangeSize={handleSizeChange}
                        masterProduct={product}
                        selectedColorLabel={currentSelection.color.name}
                        selectedProductQuantity={1}
                        size={currentSelection.size}
                        sizeList={
                          currentSelection.hasMultipleOneSizes
                            ? formatSizeListForOneSize({
                                isCurvy,
                                sizeList: currentSelection.color.sizes,
                              })
                            : currentSelection.color.sizes
                        }
                        isSet
                        isCombinedSet={hasLinkedProduct(currentProduct)}
                        setId={product.master_product_id}
                      />
                    </SetItemDetail>
                  </ProductSetDetailOptionSummary>
                </ProductSetDetailOptionSummaryWrapper>
              </RowCollapsible>
            </SetItemHeader>
          </SetItem>
        </SetItemWrapper>
      );
    });
  };

  const renderSingleProductDetails = useCallback(() => {
    const currentSelection = selections
      ? selections[defaultItem?.masterProductId]
      : null;

    // Product can get unset, so we need to check if it exists
    // This was causing an error when closing the modal while only partial
    // product data was loaded
    if (!currentSelection) {
      return null;
    }
    return (
      <>
        {isDesktop && isGwpModeEnabled ? (
          <StyledProductDetailName
            product={currentProduct}
            isProductFlagHidden
          />
        ) : null}
        <SingleProductColorSwatches
          id={currentProduct.master_product_id}
          isBorderfreeCustomer={isBorderfreeCustomer}
          isGwpSelection={isGwpModeEnabled}
          isSoldOut={isSoldOut}
          limit={10}
          onChangeColor={handleColorChange}
          masterProduct={currentProduct}
          selectedColor={currentSelection.color.value}
          selectedColorLabel={currentSelection.color.name}
          selectedSize={currentSelection.size}
          showSelectedOnly={shouldHideRelatedProducts}
          shouldHideColorSwatches={isGwpModeEnabled}
        />
        <StyledProductDetailSize
          displayBraSize={hasBraSize(currentProduct)}
          error={currentSelection.size.error}
          id={currentProduct.master_product_id}
          isGwpSelection={isGwpModeEnabled}
          isSoldOut={isSoldOut}
          hideQuantity
          onChangeSize={handleSizeChange}
          masterProduct={currentProduct}
          selectedColorLabel={currentSelection.color.name}
          selectedProductQuantity={1}
          shouldHideFitAndSizeGuide={false}
          shouldSplitCombinedSize
          size={currentSelection.size}
          sizeList={
            currentSelection.hasMultipleOneSizes
              ? formatSizeListForOneSize({
                  isCurvy,
                  sizeList: currentSelection.color.sizes,
                })
              : currentSelection.color.sizes
          }
        />
      </>
    );
  }, [
    selections,
    defaultItem,
    isDesktop,
    currentProduct,
    isBorderfreeCustomer,
    isSoldOut,
    handleColorChange,
    shouldHideRelatedProducts,
    handleSizeChange,
    isCurvy,
    isGwpModeEnabled,
  ]);

  const modalHeaderForMobile = useMemo(
    () =>
      currentProduct && isMobile ? (
        <MobileModalHeader
          onTouchStart={e => handleTouchStart(e, ModalTouchAreas.MODAL_HEADER)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          mobileFullScreen={mobileFullScreen}
        >
          <StyledToastMessages />
          <MobileHeaderImageWrapper>
            <MobileHeaderProductImage
              angle="LAYDOWN"
              product={currentProduct}
              size={6}
            />
          </MobileHeaderImageWrapper>
          <StyledProductDetailName product={currentProduct} />
          <CloseButton onClick={handleCloseQuickViewModal} />
        </MobileModalHeader>
      ) : null,
    [
      currentProduct,
      isMobile,
      handleCloseQuickViewModal,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    ]
  );

  const priceDetails = useSelector(state =>
    getPriceDetails(state, { product: currentProduct })
  );

  return (
    <StyledModal
      isOpen={isOpen}
      beforeDialog={modalHeaderForMobile}
      dialogProps={{
        isReady: !isLoading,
      }}
      onExit={handleCloseQuickViewModal}
      shouldHideCloseButton={isMobile}
      title={formatMessage({
        id: 'global_checkout.quick_view_modal',
        defaultMessage: 'Quick view modal',
      })}
      margin={currentMargin}
    >
      {isMobile ? null : <StyledToastMessages />}
      {!isLoading && currentProduct && selections ? (
        <ModalContent
          className="modal-content"
          margin={currentMargin}
          onTouchStart={e => handleTouchStart(e, ModalTouchAreas.MODAL_CONTENT)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          ref={modalContentRef}
          {...(isGwpModeEnabled && !currentProduct
            ? {}
            : {
                'data-cnstrc-product-detail': true,
                'data-cnstrc-item-id': `${currentProduct?.master_product_id}`,
                'data-cnstrc-item-name': currentProduct?.label,
                'data-cnstrc-item-price': priceDetails.displayPrice,
              })}
        >
          <LeftColumn>
            {isMobile && !isGwpModeEnabled ? (
              <StyledProductFlag product={currentProduct} />
            ) : null}
            <ImageCarousel
              options={{
                dots: true,
                slidesToShow: isMobile ? 1.5 : 1,
                slidesToScroll: isMobile ? 1.5 : 1,
                nextArrow: (
                  <NextButton>
                    <NewThemeIcon type="caretRight" size={24} />
                  </NextButton>
                ),
                prevArrow: (
                  <PrevButton>
                    <NewThemeIcon type="caretLeft" size={24} />
                  </PrevButton>
                ),
              }}
            >
              {[1, 'LAYDOWN'].map((angle, index) => {
                return (
                  <StyledProductImage
                    key={index}
                    size={8}
                    angle={angle}
                    product={currentProduct}
                  />
                );
              })}
            </ImageCarousel>
          </LeftColumn>
          <RightColumn id="rightColumn">
            {isGwpModeEnabled ? null : (
              <ProductDetailHeader
                product={currentProduct}
                isProductSet={isProductSet}
                shouldHideReviewStars
                shouldHideFavButton
                hideAfterpay={hideAfterpay}
              />
            )}
            <ProductDetailsWrapper>
              {isProductSet ? renderSetDetails() : renderSingleProductDetails()}
              {isFlowInitiated && currentProductMasterProductId ? (
                <>
                  <ByoCTAs
                    checkByoErrorState={() => {}}
                    masterProduct={currentProduct}
                    productToAdd={
                      selections[defaultItem.masterProductId]?.size?.sizeProduct
                    }
                    selectedProductQuantity={1}
                  />
                </>
              ) : (
                <>
                  <StyledPreorderShipDate product={currentProduct} />
                  {isAddToCartGwpEnabled && (
                    <UpdateButton
                      isSet={isProductSet}
                      onClick={handleAddToCart}
                    >
                      <FormattedMessage
                        id="global_checkout.quick_view_add_gift_button"
                        defaultMessage="Add Gift To Bag"
                      />
                    </UpdateButton>
                  )}
                  {(isUpdateCartItemEnabled || isUpdateCartItemGwpEnabled) && (
                    <UpdateButton
                      onClick={
                        showAutoBundleVersion
                          ? handleUpdateCartItem
                          : handleUpdateBundleItem
                      }
                      data-cnstrc-btn={
                        showAutoBundleVersion ? 'update_item' : 'save_changes'
                      }
                      variant="primaryLavender"
                    >
                      {showAutoBundleVersion ? (
                        <FormattedMessage
                          id="global_checkout.quick_view_update_button"
                          defaultMessage="Update Item"
                        />
                      ) : (
                        <FormattedMessage
                          id="global_checkout.quick_view_save_changes_button"
                          defaultMessage="Save Changes"
                        />
                      )}
                    </UpdateButton>
                  )}
                  {isAddToCartUpsellEnabled && (
                    <ProductDetailCTA
                      onAddToCart={handleAddToCart}
                      constructorButtonLabel="add_to_bag"
                    >
                      <FormattedMessage
                        id="global_checkout.quick_view_add_to_cart"
                        defaultMessage="Add to Bag"
                      />
                    </ProductDetailCTA>
                  )}
                  {showAutoBundleVersion ? (
                    isGwpModeEnabled ? null : (
                      <StyleProductLink
                        pSource={source}
                        product={currentProduct}
                      >
                        <StyledLink onClick={handleDetailsLinkClick}>
                          <FormattedMessage
                            id="global_checkout.quick_view_full_details_link"
                            defaultMessage="View Full Details"
                          />
                        </StyledLink>
                      </StyleProductLink>
                    )
                  ) : (
                    <StyledRemoveLink onClick={handleRemoveFromBundle}>
                      <FormattedMessage
                        id="global_checkout.quick_view_remove_from_bundle"
                        defaultMessage="Remove Item From Bag"
                      />
                    </StyledRemoveLink>
                  )}
                  {isGwpModeEnabled ? (
                    <ProductDetailDescriptionWrapper>
                      <ProductDetailDescription
                        product={currentProduct}
                        isGwpQuickView={isGwpModeEnabled}
                        isProductSet={isProductSet}
                        shouldShowShippingExchange={false}
                      />
                    </ProductDetailDescriptionWrapper>
                  ) : null}
                </>
              )}
            </ProductDetailsWrapper>
          </RightColumn>
        </ModalContent>
      ) : (
        <SpinningLoader isLoading />
      )}
    </StyledModal>
  );
}

QuickViewModal.propTypes = {
  showToastMessage: PropTypes.func,
  hideToastMessage: PropTypes.func,
  mobileFullScreen: PropTypes.bool,
  hideAfterpay: PropTypes.bool,
};

export default toastWrapper(QuickViewModal);
